'use client';

/**
 * useToolTimeoutFlow — the tool-timeout recovery flow's state/submission
 * logic (view machine, retry-outcome detection, submit handlers).
 *
 * ── SHARED SINGLE INSTANCE (FINDING A) ──
 *
 * This hook is called EXACTLY ONCE per session, inside ChatProvider
 * (contexts/ChatContext.tsx) — NOT separately by each renderer. Both
 * surfaces that can show this flow (tool-timeout-card.tsx in the transcript,
 * always mounted; voice/call-tool-timeout-panel.tsx in the call overlay,
 * mounted only while a call is up) are now pure presentational consumers of
 * ChatContext.toolTimeoutFlow — they switch on `flow.state.view` to pick a
 * leaf, but neither owns any state of its own. This was NOT always true —
 * see git history / the task report for the divergence bug a per-mount
 * instance caused.
 *
 * Since this hook is no longer remounted per-incident (no more `key={...}`
 * unmount/remount to reset it for free), it detects a NEW incident itself —
 * see the `toolName`/`retryId`-watching reset effects below.
 *
 * ── RETRY-OUTCOME CORRELATION (replaces the old turnState inference) ──
 *
 * The backend mints a fresh `retryId` on every ToolTimeoutOfferEvent,
 * echoes it back on RetryToolCallMessage ("Try again"), and resolves it
 * with an explicit ToolRetryOutcomeEvent (`retryOutcome` below) carrying
 * one of four results:
 *
 *   SUCCEEDED  -> RECOVERED (the tool's real result renders via the
 *                 tool-registry path elsewhere; this card's job is done).
 *                 Emitted whenever the tool succeeds and matches the
 *                 pending incident, REGARDLESS of whether the user ever
 *                 tapped "Try again" — covers the tool quietly recovering
 *                 on its own while the card still sits on OFFER (never
 *                 in RETRYING). Correlation is on `retryId` alone, which
 *                 is set from OFFER onward (not just once a retry is in
 *                 flight — see `retryId` below), so this "arrives while
 *                 on OFFER" case needs no special handling here; it falls
 *                 out of the same correlation the other three results use.
 *   FAILED     -> settles the CURRENT incident as FAILED. The follow-up
 *                 offer that immediately follows (backend's documented
 *                 order: outcome BEFORE the new offer) carries a bumped
 *                 attempt + a NEW retryId, which the retryId-reset effect
 *                 below lands on the same FAILED view via the ordinary
 *                 attempt-based initial-state mapping — a safety net in
 *                 case the outcome event itself was dropped, not the
 *                 primary mechanism.
 *   ABANDONED  -> the model ended its turn without ever calling the tool
 *                 (a realtime model cannot be forced to invoke a specific
 *                 tool). Nothing actually failed, so this returns to
 *                 whichever card the retry was launched FROM (OFFER or
 *                 FAILED, per `state.origin`) via ToolTimeoutAction.
 *                 RETRY_ABANDONED — never RECOVERED, never stranded in
 *                 RETRYING. NOW MEANS TWO THINGS the backend doesn't
 *                 distinguish on the wire: the ordinary case (the model
 *                 genuinely just never got to it — gets
 *                 RETRY_DID_NOT_GO_THROUGH_MESSAGE, same string REJECTED
 *                 uses) and the SUPERSEDED case (a second tool timed out
 *                 while this one's retry was still in flight — gets
 *                 RETRY_ORPHANED_MESSAGE instead, shown on the NEW
 *                 incident's card, not this one). Told apart from LOCAL
 *                 OBSERVABLE STATE, not inference: superseded means
 *                 `state.view === RETRYING` at the moment THIS outcome
 *                 resolves AND the very next offer names a DIFFERENT
 *                 tool — both directly observable, not guessed. See the
 *                 toolName-reset effect below for where the second half
 *                 of that check lives (the offer arrives as a separate
 *                 event, strictly after this one — verified backend frame
 *                 order: [offer A, outcome ABANDONED(A), offer B]).
 *   REJECTED   -> the backend declined to run the retry at all (cap
 *                 exhausted, an unknown/dead session, or the redrive
 *                 itself throwing before it ever reached the model — NOT
 *                 "we tried and it failed", which is FAILED, and NOT "the
 *                 model never got to it", which is ABANDONED: here the
 *                 request to retry never ran). SAME view transition as
 *                 ABANDONED (RETRY_REJECTED, back to `origin`, never
 *                 RECOVERED) and the SAME user-facing message
 *                 (`RETRY_DID_NOT_GO_THROUGH_MESSAGE`) as ordinary
 *                 ABANDONED — see that constant's own doc comment for why
 *                 they intentionally share copy. DECISION: retry stays
 *                 offered afterward purely because `retry_available`
 *                 (unaffected by this outcome — it isn't even a field on
 *                 the event) says so; this hook does NOT hide/disable
 *                 "Try again" after a REJECTED on its own — the docstring
 *                 for ToolRetryResult.REJECTED in aol_schemas.ws_events is
 *                 explicit that the same retryId "remains valid for a
 *                 further tap when retry_available still allows it", and
 *                 inventing a second client-side cap rule here would
 *                 contradict the single-source-of-truth design
 *                 `retry_available` already exists for.
 *
 * `retryId` is INCIDENT-scoped, not tap-scoped — it stays constant across
 * multiple retries of the same offer (abandoned -> tap again -> succeeded
 * all share one id). The outcome-handling effect below correlates
 * `retryOutcome.retryId` against the CURRENT incident's `retryId` (a prop,
 * not `state.view`) before acting — this is deliberate on two counts:
 *
 *   1. CORRELATION: a mismatched id means the outcome belongs to a stale/
 *      superseded incident and must be ignored — the entire point of
 *      minting the id in the first place (see ToolRetryOutcomeEvent's
 *      docstring in aol_schemas.ws_events for the exact failure modes this
 *      replaces — turnState going idle for a dropped socket, an unrelated
 *      turn finishing, or a barge-in used to read as a false SUCCESS).
 *   2. FINDING 3 (the stranding bug): correlating on `retryId` instead of
 *      gating on `state.view === RETRYING` means an outcome is handled
 *      correctly no matter which view the user has since navigated to
 *      (OFFER -> Try again -> RETRYING -> "Skip & leave details" -> CHOOSE
 *      -> Back -> OFFER, while the retry is still in flight, no longer
 *      swallows the eventual outcome).
 *
 * Outcomes are LIVE-ONLY (never persisted, never restored) and idempotent:
 * a repeated delivery of the same (retryId, result) pair is a no-op on the
 * second arrival (see `lastProcessedOutcomeKeyRef`).
 *
 * ── FINDING 5 (RETRYING with no escape) ──
 *
 * `onRetry` (ChatContext.retryToolCall) now reports whether the frame was
 * actually SENT (the socket was open), not just whether it was requested.
 * `handleRetry`/`handleResume` only dispatch RETRY/RESUME — entering
 * RETRYING — when the send genuinely succeeded; otherwise the view stays
 * on OFFER/FAILED with an honest inline message, since a frame that never
 * left the browser can never produce an outcome to resolve it. See
 * `RETRY_UNAVAILABLE_MESSAGE`.
 *
 * The RESTORED-offer case (a retryId from a session the current backend
 * connection has no memory of — pending_tool_retry is never persisted) is
 * handled a layer up, not here: lib/tool-timeout-restore.ts's
 * resolveEffectiveToolTimeoutOffer forces `retryAvailable: false` on a
 * restored-only offer, so "Try again" never even renders for one — see
 * that function's own doc comment for the full reasoning.
 *
 * ── RECONNECT RESET (a THIRD, related case) ──
 *
 * A live offer that survives a genuine socket drop-and-reconnect (same
 * session_id reused — this is NOT the restored-offer case above, which is
 * about a page RELOAD) hits the identical underlying problem for a
 * different reason: the backend's `pending_tool_retry[session_id]` entry
 * is popped in `_teardown_state` on disconnect and never repopulated on
 * reconnect. ChatContext.onSocketReconnected observes this (via
 * useAssistantConnection's `ws.onopen`, gated to fire only on opens AFTER
 * the first) and forces `retryAvailable: false` on the CURRENT incident
 * the same way a restored offer gets forced — see `retryStaleAfterReconnect`
 * below. The one thing that override alone can't fix: a retry already
 * showing RETRYING (spinner) has no button to hide, so the dedicated
 * effect below ALSO snaps it back to OFFER/FAILED (never RECOVERED, never
 * stranded) via `ToolTimeoutAction.CONNECTION_RESET` — but ONLY from
 * RETRYING, not from CHOOSE/MESSAGE/CALLBACK, since a reconnect landing
 * after the user has already moved on to leaving details must not
 * interrupt them.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  INITIAL_TOOL_TIMEOUT_STATE,
  nextToolTimeoutState,
  ToolTimeoutAction,
  ToolTimeoutOrigin,
  ToolTimeoutView,
  type ToolTimeoutState,
} from '@/lib/tool-timeout-view';
import {
  CallbackWindow,
  submitSupportRequest,
  SupportRequestType,
} from '@/lib/api/support-requests';
import {
  MessageRole,
  ToolRetryResult,
  type Message,
  type ToolRetryOutcomeState,
  type ToolTimeoutOfferState,
} from '@/types/chat';

/** Views that still await a user decision — the only ones the auto-collapse
 *  (IGNORE) effect below ever fires from. */
const AWAITING_DECISION: ReadonlySet<ToolTimeoutView> = new Set([
  ToolTimeoutView.OFFER,
  ToolTimeoutView.FAILED,
  ToolTimeoutView.CHOOSE,
  ToolTimeoutView.MESSAGE,
  ToolTimeoutView.CALLBACK,
]);

const GENERIC_ERROR = 'Something went wrong. Please try again.';

/** FINDING 5 — shown on OFFER/FAILED (in place of dispatching RETRY) when
 *  retryToolCall couldn't actually send the frame. Deliberately does NOT
 *  claim the retry is queued or will happen automatically — nothing here
 *  retries on the caller's behalf; the button stays live so the user can
 *  simply tap it again once reconnected.
 *
 *  Copy note (governing principle: "Sara speaks in the user's world, never
 *  ours"): Sara genuinely CANNOT speak here — there is no live connection
 *  for her to say anything over — so this describes the CONNECTION, not
 *  her, and stays impersonal rather than putting words in her mouth she
 *  never said. No "tap" (wrong on desktop, where this same message renders
 *  identically — see the voice surface's own error slot). Contrast
 *  RETRY_REJECTED_MESSAGE below, where she IS connected and speaks in
 *  first person. */
const RETRY_UNAVAILABLE_MESSAGE =
  "You're offline right now — try again once you're back.";

/** Shown on OFFER/FAILED when a ToolRetryOutcomeEvent resolves with
 *  REJECTED (the backend declined to run the retry at all — cap
 *  exhausted, an unknown/dead session, or the redrive itself throwing
 *  before it ever reached the model) OR with the ORDINARY flavor of
 *  ABANDONED (the model had the directive and simply never called the
 *  tool — see the ABANDONED case below for the OTHER flavor, superseded
 *  by a different tool, which gets RETRY_ORPHANED_MESSAGE instead, not
 *  this one). Both are, from the user's chair, the exact same fact — "I
 *  asked for a retry and nothing came of it" — regardless of which of the
 *  two backend-side reasons caused it, so they intentionally share one
 *  string rather than drawing a distinction the user has no use for.
 *  Deliberately does NOT say the lookup was attempted or failed — nothing
 *  ran — and does NOT reference "Try again" by name, since
 *  `retry_available` governs independently of this message whether that
 *  button is even showing.
 *
 *  Copy note: unlike RETRY_UNAVAILABLE_MESSAGE above, the socket IS open
 *  here — Sara is connected, so this speaks AS her, in first person
 *  ("want me to"), matching the voice surface's own "Want me to try
 *  again, or have a live agent follow up?" phrasing. "Want me to", not
 *  "shall I" — the latter reads British; this product is US English. */
const RETRY_DID_NOT_GO_THROUGH_MESSAGE =
  "That didn't go through — want me to try again?";

/** The "orphaned retry" case: a SECOND tool times out while a FIRST tool's
 *  retry is still in flight (state.view === RETRYING for tool A when an
 *  offer for a DIFFERENT tool B arrives) — the backend's own ABANDONED
 *  emission for A's retry_id doesn't distinguish "the model just never
 *  got to it" from "a different tool superseded it" (see the ABANDONED
 *  case below for how this hook tells them apart from LOCAL OBSERVABLE
 *  STATE — the exact backend frame order is [offer A, outcome
 *  ABANDONED(A), offer B]). Shown on tool B's FRESH offer card (what the
 *  user is now actually looking at), not tool A's now-defunct one — see
 *  the toolName-reset effect below. User-approved copy, verbatim. */
export const RETRY_ORPHANED_MESSAGE =
  "Sorry — I'm having trouble looking things up right now.";

/** State mapping (task brief, verbatim): attempt 1 -> OFFER; attempt >= 2 ->
 *  FAILED-AGAIN. Needed for more than just the initial mount: a RESTORED
 *  offer (lib/tool-timeout-restore.ts) can arrive with attempt already at 2
 *  or higher — the first failure happened in an earlier session, before
 *  this hook ever mounted — so `INITIAL_TOOL_TIMEOUT_STATE` (always OFFER)
 *  would be wrong there. A fresh incident (a genuinely different toolName,
 *  or a new retryId on the same tool) reuses this too. */
function initialToolTimeoutState(attempt: number): ToolTimeoutState {
  return attempt >= 2
    ? { view: ToolTimeoutView.FAILED, origin: ToolTimeoutOrigin.FAILED }
    : INITIAL_TOOL_TIMEOUT_STATE;
}

export interface UseToolTimeoutFlowOptions {
  /** Identifies WHICH TOOL this incident is for. A change from the
   *  previously-known value resets the WHOLE machine (state + every
   *  detection ref) back to `initialToolTimeoutState(attempt)` — this hook
   *  is no longer remounted per-incident (see file header, "SHARED SINGLE
   *  INSTANCE"). The empty string is the explicit "no active incident"
   *  sentinel: the machine just idles at its initial view, unrendered by
   *  every consumer (both ToolTimeoutCard and CallToolTimeoutPanel gate on
   *  the offer being present before rendering anything driven by this
   *  hook's output). */
  toolName: string;
  attempt: number;
  /** No longer read by this hook's own logic — auto-collapse applies
   *  uniformly whether or not retry is still on offer (see that effect's
   *  lead comment). Kept in the options because ChatContext passes the
   *  whole offer-derived bundle here; the rendering side reads it via
   *  ToolTimeoutFlowContextValue's `offer.retryAvailable`. */
  retryAvailable: boolean;
  /** Identifies WHICH INCIDENT this is — constant across multiple retries
   *  of the SAME offer, a fresh value only when the tool times out again
   *  (see file header). Drives both the outcome-correlation guard and the
   *  retry_tool_call payload. */
  retryId: string;
  conversationId: string;
  /** The current transcript — used ONLY for the auto-collapse-on-ignore
   *  effect (a new USER-authored message arriving after mount). */
  messages: readonly Message[];
  /** ChatContext.retryToolCall — sends the retry_tool_call frame (carrying
   *  `retryId`) and reports whether it was actually SENT. See file header,
   *  "FINDING 5". */
  onRetry: (retryId: string) => boolean;
  /** The most recent ToolRetryOutcomeEvent this session has seen, or null.
   *  Correlated against `retryId` before this hook acts on it — see file
   *  header. */
  retryOutcome: ToolRetryOutcomeState | null;
  /** Session-recovery (reconnect reset): true only while a genuine
   *  reconnect has invalidated THIS specific `retryId` — see
   *  ChatContext.onSocketReconnected's doc comment for the full "why"
   *  (the backend's pending_tool_retry bookkeeping is per-connection, not
   *  per-incident). ChatContext also folds this into `retryAvailable`
   *  above (forces it false) — this hook only needs it separately to
   *  snap a live RETRYING spinner back to OFFER/FAILED, which
   *  `retryAvailable` alone can't drive (RETRYING doesn't render a retry
   *  button to hide). See the dedicated effect below. */
  retryStaleAfterReconnect: boolean;
  /** The name behind any contact record already on file this session, if
   *  any — see tool-timeout-card.tsx's ToolTimeoutCardProps.nameOnFile doc
   *  comment for the full "why". `phoneOnFile` has NO equivalent here — it
   *  never drove any of this hook's own logic (only ToolTimeoutCallback's
   *  rendering, a leaf each caller wires directly) — see
   *  ToolTimeoutFlowContextValue's own `phoneOnFile` field. */
  nameOnFile?: string;
}

export interface UseToolTimeoutFlowResult {
  state: ToolTimeoutState;
  /** Fires the given action against the machine (and clears any submit
   *  error) — exposed raw so each container can dispatch the navigation
   *  actions (LEAVE_DETAILS/CHOOSE_MESSAGE/CHOOSE_CALLBACK/BACK) its own
   *  leaves need, without this hook needing to know about every button. */
  dispatch: (action: ToolTimeoutAction) => void;
  submitting: boolean;
  error: string | null;
  submittedEmail: string | undefined;
  submittedPhone: string | undefined;
  submittedWindow: CallbackWindow | undefined;
  handleRetry: () => void;
  handleResume: () => void;
  handleSubmitMessage: (values: {
    name: string;
    email: string;
    body: string;
  }) => Promise<void>;
  handleSubmitCallback: (values: {
    name: string;
    phone: string;
    email?: string;
    callbackWindow: CallbackWindow;
  }) => Promise<void>;
}

/** The full shape ChatContext.toolTimeoutFlow exposes — this hook's own
 *  result plus the two rendering-only fields neither surface's logic
 *  touches (`offer.retryAvailable` for gating, `phoneOnFile` for
 *  ToolTimeoutCallback's prefill). Built by ChatContext, not this hook —
 *  see that file's `toolTimeoutFlow` useMemo. */
export interface ToolTimeoutFlowContextValue extends UseToolTimeoutFlowResult {
  offer: ToolTimeoutOfferState;
  phoneOnFile?: string;
}

export function useToolTimeoutFlow({
  toolName,
  attempt,
  retryId,
  conversationId,
  messages,
  onRetry,
  retryOutcome,
  retryStaleAfterReconnect,
  nameOnFile,
}: UseToolTimeoutFlowOptions): UseToolTimeoutFlowResult {
  const [state, setState] = useState<ToolTimeoutState>(() =>
    initialToolTimeoutState(attempt)
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submittedEmail, setSubmittedEmail] = useState<string | undefined>();
  const [submittedPhone, setSubmittedPhone] = useState<string | undefined>();
  const [submittedWindow, setSubmittedWindow] = useState<
    CallbackWindow | undefined
  >();

  const dispatch = useCallback((action: ToolTimeoutAction) => {
    setError(null);
    setState((prev) => nextToolTimeoutState(prev, action));
  }, []);

  const mountLenRef = useRef(messages.length);

  // ── Orphan tracking (session-recovery): set by the outcome-correlation
  // effect below the instant an ABANDONED resolves a retry that was
  // ACTUALLY in flight (state.view === RETRYING) — a candidate for "this
  // incident is about to be superseded by a different tool", per the file
  // header's ABANDONED note. Purely a fact about what JUST happened, not a
  // prediction: the toolName-reset effect immediately below consumes it
  // (applies RETRY_ORPHANED_MESSAGE and resets to false) the moment a
  // DIFFERENT tool's offer actually confirms the supersession; the
  // retryId-only reset effect after it ALSO consumes it (silently, no
  // orphan message) for the case where it turns out NOT to have been a
  // supersession after all (the SAME tool simply timed out again) — either
  // way this flag never survives past the very next incident-defining
  // event, so it can't leak into some unrelated LATER abandonment. ──
  const abandonedWhileRetryingRef = useRef(false);

  // ── New-tool reset (FINDING A follow-on): a genuinely different toolName
  // is a brand-new incident on a DIFFERENT tool — full reset of state and
  // every detection ref. Declared first so it settles before the retryId
  // effect below runs on the same render (both refs get caught up
  // together; the retryId effect then sees its own ref already current and
  // no-ops instead of double-resetting). ──
  const knownToolNameRef = useRef(toolName);
  const knownRetryIdRef = useRef(retryId);
  useEffect(() => {
    if (toolName === knownToolNameRef.current) return;
    knownToolNameRef.current = toolName;
    knownRetryIdRef.current = retryId;
    mountLenRef.current = messages.length;
    setState(initialToolTimeoutState(attempt));
    setSubmitting(false);
    // Session-recovery (orphaned retry): a DIFFERENT tool's offer just
    // confirmed the previous incident's ABANDONED was a supersession, not
    // an ordinary one — see `abandonedWhileRetryingRef`'s own doc comment.
    // Shown on THIS (the new incident's) card, per the coordinator's
    // explicit "that is what the user is now looking at".
    setError(abandonedWhileRetryingRef.current ? RETRY_ORPHANED_MESSAGE : null);
    abandonedWhileRetryingRef.current = false;
    setSubmittedEmail(undefined);
    setSubmittedPhone(undefined);
    setSubmittedWindow(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toolName]);

  // ── New-incident-on-the-SAME-tool reset: a repeat timeout mints a NEW
  // retryId without changing toolName. Safety net only — the backend's
  // documented emission order sends the FAILED outcome for the OLD retryId
  // BEFORE this follow-up offer, so the outcome effect below has normally
  // already landed on FAILED by the time this fires; this just guarantees
  // the right view via the SAME attempt-based mapping even if that outcome
  // was somehow dropped. Also clears submit state — a fresh incident on the
  // same tool shouldn't carry a stale submitted-form receipt forward. ──
  useEffect(() => {
    if (retryId === knownRetryIdRef.current) return;
    knownRetryIdRef.current = retryId;
    setState(initialToolTimeoutState(attempt));
    setSubmitting(false);
    setError(null);
    // Session-recovery (orphaned retry): a repeat timeout on the SAME
    // tool proves the earlier ABANDONED (if any) was NOT a cross-tool
    // supersession after all — consume the flag silently, no orphan
    // message here (see `abandonedWhileRetryingRef`'s own doc comment).
    abandonedWhileRetryingRef.current = false;
    setSubmittedEmail(undefined);
    setSubmittedPhone(undefined);
    setSubmittedWindow(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryId]);

  // ── Retry-outcome correlation (see file header). Idempotent via a
  // (retryId, result) key — a repeated delivery of the same outcome is a
  // no-op on the second arrival. Correlates on `retryId`, NOT `state.view`
  // — see file header, "FINDING 3". ──
  const lastProcessedOutcomeKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!retryOutcome) return;
    const key = `${retryOutcome.retryId}:${retryOutcome.result}`;
    if (key === lastProcessedOutcomeKeyRef.current) return;
    lastProcessedOutcomeKeyRef.current = key;
    if (retryOutcome.retryId !== retryId) return; // stale/foreign incident
    switch (retryOutcome.result) {
      case ToolRetryResult.SUCCEEDED:
        dispatch(ToolTimeoutAction.RETRY_SUCCEEDED);
        break;
      case ToolRetryResult.FAILED:
        dispatch(ToolTimeoutAction.RETRY_FAILED);
        break;
      case ToolRetryResult.ABANDONED: {
        // Read BEFORE dispatch (which schedules the view transition for
        // the NEXT render) — this is the "was a retry ACTUALLY in
        // flight" fact the orphan/ordinary split needs. See the file
        // header's ABANDONED note and abandonedWhileRetryingRef's own
        // doc comment for the full mechanism.
        const wasRetrying = state.view === ToolTimeoutView.RETRYING;
        // dispatch() itself clears `error` (see its own definition) — set
        // the honest message AFTER, not before, so it survives the same
        // batch rather than being wiped by dispatch's own setError(null).
        // Set unconditionally (not just when wasRetrying): ABANDONED can
        // also resolve a retry the user has since navigated away from
        // (Finding 3), and the message is equally true either way. If
        // this incident turns out to have been superseded, the
        // toolName-reset effect below overwrites this a moment later with
        // RETRY_ORPHANED_MESSAGE on the NEW card — this one never
        // actually gets seen in that case, which is fine.
        dispatch(ToolTimeoutAction.RETRY_ABANDONED);
        setError(RETRY_DID_NOT_GO_THROUGH_MESSAGE);
        if (wasRetrying) abandonedWhileRetryingRef.current = true;
        break;
      }
      case ToolRetryResult.REJECTED:
        // dispatch() itself clears `error` (see its own definition) — set
        // the honest message AFTER, not before, so it survives the same
        // batch rather than being wiped by dispatch's own setError(null).
        dispatch(ToolTimeoutAction.RETRY_REJECTED);
        setError(RETRY_DID_NOT_GO_THROUGH_MESSAGE);
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryOutcome, retryId, dispatch]);

  // ── Reconnect reset (session-recovery): stop a spinner that can now
  // never resolve. ChatContext already forces `retryAvailable: false` for
  // this incident (handles OFFER/FAILED's "do NOT re-offer retry"); this
  // effect handles the ONE thing that alone can't: RETRYING renders no
  // retry button to hide, so a live retry attempt needs an explicit view
  // transition, not just a prop change. Fires ONLY on the false -> true
  // edge (a plain `if (!retryStaleAfterReconnect) return` would ALSO fire
  // every time it goes back to false for a fresh incident — not what this
  // guards). Reachable ONLY from RETRYING, deliberately: a reconnect
  // landing while the user has already moved on to CHOOSE/MESSAGE/
  // CALLBACK must NOT yank them back to OFFER — unlike RETRY_ABANDONED/
  // RETRY_REJECTED above, this isn't resolving a specific outcome the user
  // is owed regardless of where they navigated; it's just "don't leave a
  // dead spinner running". ──
  const knownRetryStaleRef = useRef(retryStaleAfterReconnect);
  useEffect(() => {
    if (retryStaleAfterReconnect === knownRetryStaleRef.current) return;
    knownRetryStaleRef.current = retryStaleAfterReconnect;
    if (!retryStaleAfterReconnect) return;
    if (state.view !== ToolTimeoutView.RETRYING) return;
    dispatch(ToolTimeoutAction.CONNECTION_RESET);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryStaleAfterReconnect]);

  // ── Auto-collapse: the user kept talking past the card. Applies
  // uniformly regardless of which surface is currently visible — see file
  // header ("SHARED SINGLE INSTANCE"): there is only one `state` now, so
  // this can't be a per-surface policy the way it briefly was mid-refactor.
  // Also uniform regardless of `retryAvailable` — a user who kept talking
  // past the card has moved on whether or not retry was still on offer,
  // and the NON-retryable cards (cap-exhausted / restored-after-reload /
  // reconnect-staled) need this path MOST: short of a successful
  // support-request submission they have no other exit, and would
  // otherwise stay pinned at the transcript tail (and keep winning the
  // voice notice slot) for the rest of the session.
  // ──
  useEffect(() => {
    if (messages.length <= mountLenRef.current) {
      mountLenRef.current = messages.length;
      return;
    }
    const newlyArrived = messages.slice(mountLenRef.current);
    mountLenRef.current = messages.length;
    const userSpoke = newlyArrived.some((m) => m.role === MessageRole.USER);
    if (!userSpoke) return;
    if (!AWAITING_DECISION.has(state.view)) return;
    dispatch(ToolTimeoutAction.IGNORE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  // ── FINDING 5: only enter RETRYING if the frame genuinely left the
  // browser. A closed socket (or, after a reload, a retryId the current
  // backend session was never told about — though that case is actually
  // prevented a layer up, see the restored-offer note in file header)
  // must never spin forever waiting for an outcome that can't arrive. ──
  const handleRetry = useCallback(() => {
    if (!onRetry(retryId)) {
      setError(RETRY_UNAVAILABLE_MESSAGE);
      return;
    }
    dispatch(ToolTimeoutAction.RETRY);
  }, [dispatch, onRetry, retryId]);

  const handleResume = useCallback(() => {
    if (!onRetry(retryId)) {
      setError(RETRY_UNAVAILABLE_MESSAGE);
      return;
    }
    dispatch(ToolTimeoutAction.RESUME);
  }, [dispatch, onRetry, retryId]);

  const handleSubmitMessage = useCallback(
    async (values: { name: string; email: string; body: string }) => {
      setSubmitting(true);
      setError(null);
      try {
        await submitSupportRequest(conversationId, {
          type: SupportRequestType.MESSAGE,
          name: values.name,
          email: values.email,
          body: values.body,
        });
        setSubmittedEmail(values.email);
        dispatch(ToolTimeoutAction.SUBMITTED);
      } catch (err) {
        setError(err instanceof Error ? err.message : GENERIC_ERROR);
      } finally {
        setSubmitting(false);
      }
    },
    [conversationId, dispatch]
  );

  const handleSubmitCallback = useCallback(
    async (values: {
      name: string;
      phone: string;
      email?: string;
      callbackWindow: CallbackWindow;
    }) => {
      setSubmitting(true);
      setError(null);
      try {
        await submitSupportRequest(conversationId, {
          type: SupportRequestType.CALLBACK,
          // The "details already on file" one-tap path always submits an
          // empty name (tool-timeout-callback.tsx's phoneOnFile branch) —
          // fall back to the name behind that SAME on-file record so the
          // request isn't created nameless. A hand-typed name from the
          // full form always wins.
          name: values.name || nameOnFile || '',
          phone: values.phone,
          email: values.email,
          callback_window: values.callbackWindow,
        });
        setSubmittedPhone(values.phone);
        setSubmittedWindow(values.callbackWindow);
        dispatch(ToolTimeoutAction.SUBMITTED);
      } catch (err) {
        setError(err instanceof Error ? err.message : GENERIC_ERROR);
      } finally {
        setSubmitting(false);
      }
    },
    [conversationId, nameOnFile, dispatch]
  );

  // Memoized so ChatContext's own `value` memo (contexts/ChatContext.tsx) —
  // whose whole point is a STABLE reference so an unrelated field changing
  // doesn't force-render every consumer — can safely depend on this as one
  // object instead of every individual field. Every field above is now
  // itself stable (state/submitting/error/... only change via their own
  // setState; every handler is useCallback'd), so this only produces a new
  // object when something actually changed.
  return useMemo(
    () => ({
      state,
      dispatch,
      submitting,
      error,
      submittedEmail,
      submittedPhone,
      submittedWindow,
      handleRetry,
      handleResume,
      handleSubmitMessage,
      handleSubmitCallback,
    }),
    [
      state,
      dispatch,
      submitting,
      error,
      submittedEmail,
      submittedPhone,
      submittedWindow,
      handleRetry,
      handleResume,
      handleSubmitMessage,
      handleSubmitCallback,
    ]
  );
}
